import { BehaviorSubject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Type, Destroyable } from '../common';
import { IGraphic } from '../core/graphic';
import { ComponentRepository } from './component.repository';

/**
 * 设计时和运行时都会使用到ComponentRepositoryManager
 * 管理设计器或运行时加载的全部组件库
 *
 * 设计器只能新建该manager中存在的Graphic
 * 运行时只能识别该manager中存在的Graphic
 */
// @dynamic
export class ComponentManager extends Destroyable {
  private static _manager: ComponentManager;

  private _map: Map<string, ComponentRepository> = new Map();
  private _paletteConfig$ = new BehaviorSubject(null);

  static getInstance() {
    if (!this._manager) {
      this._manager = new ComponentManager();
    }
    return this._manager;
  }

  private constructor() {
    super();
    this.onDestroy(() => {
      if (this._map) {
        this._map.clear();
        this._map = null;
      }
    });
  }

  addRepository(repo: ComponentRepository) {
    if (this.usable && repo) {
      this._map.set(repo.id, repo);
      this._updatePaletteConfig();
    }
  }

  removeRepository(repo: ComponentRepository) {
    if (this.usable && repo) {
      this._map.delete(repo.id);
    }
  }

  getRepository(id: string) {
    return this._map.get(id);
  }

  has(path: string) {
    const [repoID, graphicID] = path.split('$');
    if (this._map.has(repoID)) {
      return this._map.get(repoID).has(graphicID);
    } else {
      return false;
    }
  }

  includes(array: Array<string>): boolean {
    const keys = Array.from(this._map.keys());
    // array.every: This method returns true for any condition put on an empty array.
    return array.every((value) => {
      return keys.includes(value);
    });
  }

  get repositories(): Array<ComponentRepository> {
    return Array.from(this._map.values());
  }

  get paletteConfig$() {
    return this._paletteConfig$.asObservable().pipe(delay(100));
  }

  /**
   * 根据路径信息获取组件元数据
   * @param {string} path
   * @returns {any}
   */
  getComponentMeta(path: string) {
    if (path) {
      const [repoID, graphicID] = path.split('$');
      if (this._map.has(repoID)) {
        return this._map.get(repoID).getComponentMeta(graphicID);
      } else {
        return null;
      }
    }
  }

  getGraphicDef(path: string): Type<IGraphic> {
    if (path) {
      const [repoID, graphicID] = path.split('$');
      if (this._map.has(repoID)) {
        return this._map.get(repoID).getComponentMeta(graphicID).graphicDef;
      } else {
        return null;
      }
    }
  }

  /**
   * 获取Graphic实例
   * @param path
   */
  getGraphicInstance(path: string) {
    if (this.has(path)) {
      return new (this.getGraphicDef(path))();
    } else {
      return null;
    }
  }

  private _updatePaletteConfig() {
    this._paletteConfig$.next(Array.from(this._map.values()).map(value => {
      return value.paletteConfig;
    }));

  }
}

export const componentManager = ComponentManager.getInstance();
